import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  TextField,
  MenuItem,
  Grid,
  Typography,
  Switch,
  FormControlLabel,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Collapse,
} from "@mui/material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import { fetchData } from "../api/client";

const DAY_KEYS = [
  { key: "mon", label: "Seg" },
  { key: "tue", label: "Ter" },
  { key: "wed", label: "Qua" },
  { key: "thu", label: "Qui" },
  { key: "fri", label: "Sex" },
  { key: "sat", label: "Sáb" },
  { key: "sun", label: "Dom" },
];

const MODALIDADES = [
  { value: "CLT", label: "CLT" },
  { value: "PJ", label: "PJ" },
  { value: "ESTAGIO", label: "Estágio" },
  { value: "AUTONOMO", label: "Autônomo" },
  { value: "OUTRO", label: "Outro" },
];

const JORNADA_TIPOS = [
  { value: "INTEGRAL", label: "Integral" },
  { value: "PARCIAL", label: "Parcial" },
  { value: "FLEXIVEL", label: "Flexível" },
  { value: "ESCALA", label: "Escala" },
];

export function defaultJornadaJson() {
  const days = {};
  DAY_KEYS.forEach(({ key }) => {
    const weekday = !["sat", "sun"].includes(key);
    days[key] = {
      enabled: weekday,
      start: weekday ? "09:00" : null,
      end: weekday ? "18:00" : null,
    };
  });
  return {
    timezone: "America/Sao_Paulo",
    break_minutes: 60,
    break_start: "12:00",
    break_end: "13:00",
    days,
  };
}

function minutesBetweenHm(start, end) {
  if (!start || !end) return null;
  const [sh, sm] = String(start).split(":").map(Number);
  const [eh, em] = String(end).split(":").map(Number);
  if (!Number.isFinite(sh) || !Number.isFinite(eh)) return null;
  const diff = eh * 60 + (em || 0) - (sh * 60 + (sm || 0));
  return diff > 0 ? diff : null;
}

export function emptyHrFields() {
  return {
    modalidade_contratacao: "",
    salario_mensal: "",
    encargos_pct: "",
    beneficios_mensal: "",
    jornada_tipo: "",
    jornada_json: null,
    funcao_confianca: null,
    regime_jornada_override: "",
    estagio_limite_diario_min: "",
    estagio_limite_semanal_min: "",
  };
}

function tinyIntOrEmpty(v) {
  if (v === true || Number(v) === 1) return 1;
  if (v === false || Number(v) === 0) return 0;
  return null;
}

export function isUserHrOverrideEmpty(h) {
  if (!h) return true;
  return (
    !h.salario_mensal &&
    !h.modalidade_contratacao &&
    !h.jornada_json &&
    h.funcao_confianca !== 1 &&
    h.funcao_confianca !== true &&
    !h.regime_jornada_override &&
    (h.estagio_limite_diario_min === "" || h.estagio_limite_diario_min == null) &&
    (h.estagio_limite_semanal_min === "" || h.estagio_limite_semanal_min == null)
  );
}

export function hrOutroOverrideMissing(h) {
  return (
    String(h?.modalidade_contratacao || "") === "OUTRO" &&
    !["CLOCK", "NO_CLOCK"].includes(String(h?.regime_jornada_override || "").toUpperCase())
  );
}

export function hrFieldsFromEntity(entity) {
  if (!entity) return emptyHrFields();
  return {
    modalidade_contratacao: entity.modalidade_contratacao || "",
    salario_mensal: entity.salario_mensal ?? "",
    encargos_pct: entity.encargos_pct ?? "",
    beneficios_mensal: entity.beneficios_mensal ?? "",
    jornada_tipo: entity.jornada_tipo || "",
    jornada_json: entity.jornada_json || null,
    funcao_confianca: tinyIntOrEmpty(entity.funcao_confianca),
    regime_jornada_override: entity.regime_jornada_override || "",
    estagio_limite_diario_min: entity.estagio_limite_diario_min ?? "",
    estagio_limite_semanal_min: entity.estagio_limite_semanal_min ?? "",
  };
}

/**
 * Seção de remuneração + jornada.
 * @param {object} props
 * @param {object} props.value — campos HR
 * @param {(patch: object) => void} props.onChange
 * @param {boolean} [props.inheritMode] — usuário: toggle herdar do cargo
 * @param {boolean} [props.inheritFromCargo]
 * @param {(v:boolean)=>void} [props.onInheritChange]
 * @param {object|null} [props.effectiveCost] — preview
 * @param {number|null} [props.positionId] — para benchmark IA
 * @param {boolean} [props.showBenchmark]
 */
export default function WorkforceHrSection({
  value,
  onChange,
  inheritMode = false,
  inheritFromCargo = false,
  onInheritChange,
  effectiveCost = null,
  positionId = null,
  showBenchmark = false,
  disabled = false,
  hideHeader = false,
}) {
  const [presets, setPresets] = useState([]);
  const [showSchedule, setShowSchedule] = useState(Boolean(value?.jornada_json));
  const [benchLoading, setBenchLoading] = useState(false);
  const [benchResult, setBenchResult] = useState(null);
  const [benchError, setBenchError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchData("/hr/contract-presets", "GET")
      .then((res) => {
        if (cancelled) return;
        const list = res?.data?.data || res?.data || [];
        setPresets(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setPresets([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const locked = disabled || (inheritMode && inheritFromCargo);
  const modalidade = String(value?.modalidade_contratacao || "");
  const isClt = modalidade === "CLT";
  const isEstagio = modalidade === "ESTAGIO";
  const isPrestador = modalidade === "PJ" || modalidade === "AUTONOMO";
  const isOutro = modalidade === "OUTRO";
  const isConfianca = isClt && Number(value?.funcao_confianca) === 1;
  const showJornadaGrid =
    (isClt && !isConfianca) ||
    isEstagio ||
    (isOutro && String(value?.regime_jornada_override || "") === "CLOCK");

  const setField = (key, v) => onChange({ ...value, [key]: v });

  const onModalidade = (mod) => {
    const preset = presets.find((p) => p.modalidade === mod);
    const patch = { ...value, modalidade_contratacao: mod };
    if (preset) {
      if (value.encargos_pct === "" || value.encargos_pct == null) {
        patch.encargos_pct = preset.encargos_pct_default;
      }
      if (value.beneficios_mensal === "" || value.beneficios_mensal == null) {
        patch.beneficios_mensal = preset.beneficios_mensal_default;
      }
    }
    if (mod !== "CLT") patch.funcao_confianca = null;
    if (mod !== "OUTRO") patch.regime_jornada_override = "";
    if (mod !== "ESTAGIO") {
      patch.estagio_limite_diario_min = "";
      patch.estagio_limite_semanal_min = "";
    }
    if (mod === "PJ" || mod === "AUTONOMO") {
      patch.jornada_json = null;
      setShowSchedule(false);
    }
    onChange(patch);
  };

  const jornada = value.jornada_json || defaultJornadaJson();

  const setDay = (dayKey, patch) => {
    const next = {
      ...jornada,
      days: {
        ...jornada.days,
        [dayKey]: { ...(jornada.days?.[dayKey] || {}), ...patch },
      },
    };
    onChange({ ...value, jornada_json: next });
  };

  const runBenchmark = async () => {
    if (!positionId) return;
    setBenchLoading(true);
    setBenchError(null);
    setBenchResult(null);
    try {
      const res = await fetchData(`/positions/${positionId}/salary-benchmark`, "POST", {});
      const b = res?.data?.benchmark || res?.benchmark;
      if (!b) throw new Error(res?.message || "Sem resultado");
      setBenchResult(b);
    } catch (e) {
      setBenchError(e?.message || "Falha no benchmark");
    } finally {
      setBenchLoading(false);
    }
  };

  const applyBenchmark = () => {
    if (!benchResult?.suggested_monthly) return;
    onChange({
      ...value,
      salario_mensal: benchResult.suggested_monthly,
    });
  };

  const preview = useMemo(() => {
    if (effectiveCost?.custo_mensal != null) return effectiveCost;
    return null;
  }, [effectiveCost]);

  return (
    <Box sx={{ mt: hideHeader ? 0 : 2 }}>
      {!hideHeader ? (
        <>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            Remuneração e jornada
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            A modalidade define custo e o regime legal de ponto no escritório virtual.
            Sem modalidade, quem tem permissão de escritório virtual usa o regime Outro sem ponto.
            {inheritMode ? " No usuário, o valor informado prevalece sobre o do cargo." : null}
          </Typography>
        </>
      ) : null}

      {inheritMode ? (
        <FormControlLabel
          control={
            <Switch
              checked={inheritFromCargo}
              onChange={(e) => onInheritChange?.(e.target.checked)}
            />
          }
          label="Usar padrão do cargo (sem override)"
          sx={{ mb: 1 }}
        />
      ) : null}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <TextField
            select
            fullWidth
            size="small"
            label="Modalidade"
            value={value.modalidade_contratacao || ""}
            disabled={locked}
            onChange={(e) => onModalidade(e.target.value)}
          >
            <MenuItem value="">—</MenuItem>
            {MODALIDADES.map((m) => (
              <MenuItem key={m.value} value={m.value}>
                {m.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Salário mensal (R$)"
            value={value.salario_mensal ?? ""}
            disabled={locked}
            onChange={(e) => setField("salario_mensal", e.target.value === "" ? "" : Number(e.target.value))}
            inputProps={{ min: 0, step: 0.01 }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Encargos (%)"
            value={value.encargos_pct ?? ""}
            disabled={locked}
            onChange={(e) => setField("encargos_pct", e.target.value === "" ? "" : Number(e.target.value))}
            inputProps={{ min: 0, step: 0.01 }}
            helperText="Vazio = preset da modalidade"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Benefícios mensais (R$)"
            value={value.beneficios_mensal ?? ""}
            disabled={locked}
            onChange={(e) =>
              setField("beneficios_mensal", e.target.value === "" ? "" : Number(e.target.value))
            }
            inputProps={{ min: 0, step: 0.01 }}
          />
        </Grid>
        {isClt ? (
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={isConfianca}
                  disabled={locked}
                  onChange={(e) => {
                    const on = e.target.checked;
                    onChange({
                      ...value,
                      funcao_confianca: on ? 1 : 0,
                      jornada_json: on ? null : value.jornada_json,
                    });
                    if (on) setShowSchedule(false);
                  }}
                />
              }
              label="Função de confiança — isenção de horário (CLT art. 62, II)"
            />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ pl: 6, mt: -0.5 }}>
              Só marque se houver cargo de gestão e gratificação no critério do art. 62, parágrafo único
              (salário do cargo de confiança pelo menos 40% acima do salário efetivo). O art. 468 §§ 1º e 2º
              regula a reversão da função e a não incorporação da gratificação — não cria a isenção de ponto.
              Este sistema não registrará horário.
            </Typography>
          </Grid>
        ) : null}
        {isOutro ? (
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              size="small"
              required
              label="Regime de horário"
              value={value.regime_jornada_override || ""}
              disabled={locked}
              onChange={(e) => setField("regime_jornada_override", e.target.value)}
              error={!locked && hrOutroOverrideMissing(value)}
              helperText={
                !locked && hrOutroOverrideMissing(value)
                  ? "Obrigatório: escolha se há controle de horário."
                  : "Obrigatório. Sem escolha o sistema não registra ponto."
              }
            >
              <MenuItem value="">—</MenuItem>
              <MenuItem value="CLOCK">Com registro de horário</MenuItem>
              <MenuItem value="NO_CLOCK">Sem jornada / sem ponto</MenuItem>
            </TextField>
          </Grid>
        ) : null}
        {isEstagio ? (
          <>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Limite diário (min)"
                value={value.estagio_limite_diario_min ?? ""}
                disabled={locked}
                onChange={(e) =>
                  setField(
                    "estagio_limite_diario_min",
                    e.target.value === "" ? "" : Number(e.target.value)
                  )
                }
                helperText="Padrão 360 (6h, art. 10, II). Ensino médio/fundamental: 240 (4h)"
                inputProps={{ min: 60, step: 30 }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Limite semanal (min)"
                value={value.estagio_limite_semanal_min ?? ""}
                disabled={locked}
                onChange={(e) =>
                  setField(
                    "estagio_limite_semanal_min",
                    e.target.value === "" ? "" : Number(e.target.value)
                  )
                }
                helperText="Padrão 1800 (30h, art. 10, II). Até 2400 só se o TCE prever"
                inputProps={{ min: 60, step: 30 }}
              />
            </Grid>
          </>
        ) : null}
        {showJornadaGrid ? (
          <>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                fullWidth
                size="small"
                label={isEstagio ? "Tipo de horário de estágio" : "Tipo de jornada"}
                value={value.jornada_tipo || ""}
                disabled={locked}
                onChange={(e) => setField("jornada_tipo", e.target.value)}
              >
                <MenuItem value="">—</MenuItem>
                {JORNADA_TIPOS.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4} sx={{ display: "flex", alignItems: "center" }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showSchedule}
                    disabled={locked}
                    onChange={(e) => {
                      const on = e.target.checked;
                      setShowSchedule(on);
                      if (on && !value.jornada_json) {
                        onChange({ ...value, jornada_json: defaultJornadaJson() });
                      }
                      if (!on) {
                        onChange({ ...value, jornada_json: null });
                      }
                    }}
                  />
                }
                label={isEstagio ? "Definir horário de estágio" : "Definir horários semanais"}
              />
            </Grid>
          </>
        ) : isPrestador ? (
          <Grid item xs={12}>
            <Alert severity="info">
              Prestador (PJ/autônomo): este sistema não registra ponto nem cadastra jornada. O escritório
              é sala de colaboração do contrato de serviço. A realidade da prestação (CLT arts. 3º e 442-B)
              prevalece sobre o rótulo do cadastro.
            </Alert>
          </Grid>
        ) : isConfianca ? (
          <Grid item xs={12}>
            <Alert severity="info">
              Função de confiança: não cadastre horário fixo. Se o empregador controlar horário na
              prática, a isenção do art. 62 pode ser afastada e cabem horas extras.
            </Alert>
          </Grid>
        ) : null}
      </Grid>

      <Collapse in={showSchedule && !locked && showJornadaGrid}>
        <Box sx={{ mt: 2, p: 2, bgcolor: "action.hover", borderRadius: 1 }}>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "flex-start", mb: 2 }}>
            <TextField
              size="small"
              type="time"
              label="Início do intervalo"
              value={jornada.break_start || ""}
              onChange={(e) => {
                const break_start = e.target.value || null;
                const mins = minutesBetweenHm(break_start, jornada.break_end);
                onChange({
                  ...value,
                  jornada_json: {
                    ...jornada,
                    break_start,
                    break_minutes: mins != null ? mins : jornada.break_minutes,
                  },
                });
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160 }}
            />
            <TextField
              size="small"
              type="time"
              label="Fim do intervalo"
              value={jornada.break_end || ""}
              onChange={(e) => {
                const break_end = e.target.value || null;
                const mins = minutesBetweenHm(jornada.break_start, break_end);
                onChange({
                  ...value,
                  jornada_json: {
                    ...jornada,
                    break_end,
                    break_minutes: mins != null ? mins : jornada.break_minutes,
                  },
                });
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160 }}
            />
            <TextField
              size="small"
              type="number"
              label="Duração (min)"
              value={jornada.break_minutes ?? 60}
              onChange={(e) =>
                onChange({
                  ...value,
                  jornada_json: { ...jornada, break_minutes: Number(e.target.value) || 0 },
                })
              }
              helperText={
                jornada.break_start && jornada.break_end
                  ? "Calculada pelo horário. O escritório virtual avisa início e retorno."
                  : "Cadastre o horário para o escritório avisar o intervalo."
              }
              sx={{ maxWidth: 200 }}
              inputProps={{ min: 0 }}
            />
          </Box>
          <Grid container spacing={1}>
            {DAY_KEYS.map(({ key, label }) => {
              const d = jornada.days?.[key] || { enabled: false };
              return (
                <Grid item xs={12} sm={6} md={4} key={key}>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={Boolean(d.enabled)}
                          onChange={(e) =>
                            setDay(key, {
                              enabled: e.target.checked,
                              start: d.start || "09:00",
                              end: d.end || "18:00",
                            })
                          }
                        />
                      }
                      label={label}
                    />
                    <TextField
                      size="small"
                      type="time"
                      disabled={!d.enabled}
                      value={d.start || "09:00"}
                      onChange={(e) => setDay(key, { start: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: 110 }}
                    />
                    <TextField
                      size="small"
                      type="time"
                      disabled={!d.enabled}
                      value={d.end || "18:00"}
                      onChange={(e) => setDay(key, { end: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: 110 }}
                    />
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Collapse>

      {preview?.custo_mensal != null ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          Custo mensal efetivo: <strong>R$ {Number(preview.custo_mensal).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</strong>
          {preview.custo_hora != null
            ? ` · Custo/hora: R$ ${Number(preview.custo_hora).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`
            : null}
          {preview.weekly_hours != null ? ` · ${preview.weekly_hours}h/semana` : null}
        </Alert>
      ) : null}

      {showBenchmark && positionId ? (
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ mb: 1.5 }} />
          <Button
            variant="outlined"
            startIcon={benchLoading ? <CircularProgress size={16} /> : <AutoAwesomeRoundedIcon />}
            disabled={benchLoading}
            onClick={runBenchmark}
          >
            Benchmark de mercado (IA)
          </Button>
          {benchError ? (
            <Alert severity="error" sx={{ mt: 1 }}>
              {benchError}
            </Alert>
          ) : null}
          {benchResult ? (
            <Alert
              severity="success"
              sx={{ mt: 1 }}
              action={
                <Button color="inherit" size="small" onClick={applyBenchmark}>
                  Aplicar sugerido
                </Button>
              }
            >
              Sugestão: R$ {Number(benchResult.suggested_monthly).toLocaleString("pt-BR")}
              {benchResult.min != null && benchResult.max != null
                ? ` (faixa ${benchResult.min}–${benchResult.max})`
                : null}
              {benchResult.rationale ? (
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  {benchResult.rationale}
                </Typography>
              ) : null}
            </Alert>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
}
